import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Courses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");

  const filters = {
    q: searchParams.get("q") || "",
    category: searchParams.get("category") || "",
    level: searchParams.get("level") || "",
    mode: searchParams.get("mode") || "",
    sort: searchParams.get("sort") || "featured",
    page: Number(searchParams.get("page") || 1),
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page: filters.page, limit: 12, sort: filters.sort };
    if (filters.q) params.q = filters.q;
    if (filters.category) params.category = filters.category;
    if (filters.level) params.level = filters.level;
    if (filters.mode) params.mode = filters.mode;
    api.get("/courses", { params })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [searchParams]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  return (
    <div className="bg-navy-base min-h-screen" data-testid="courses-page">
      <SEO title="Courses in Jorhat — AI, IT, Tally, Electronics, Beauty & Wellness | XTM TECHNOLOGIES"
        description="Browse all career courses at XTM FutureSkills Academy Jorhat: programming, AI, data science, cybersecurity, Tally, hardware, beauty & wellness, WILP B.Voc and more." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Course Catalogue</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6" data-testid="courses-heading">
            Find Your Program
          </h1>
          <form onSubmit={(e) => { e.preventDefault(); setFilter("q", q); }} className="glass rounded-2xl p-2 flex items-center gap-2 max-w-2xl">
            <Search className="w-5 h-5 text-orange-400 ml-3 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses, skills, tools…"
              className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-sm py-2.5" data-testid="courses-search-input" />
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="courses-search-btn">Search</Button>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-wrap gap-3 mb-8 items-center" data-testid="courses-filters">
          <Select value={filters.category || "all"} onValueChange={(v) => setFilter("category", v)}>
            <SelectTrigger className="w-56 bg-navy-surface border-white/15 text-slate-200" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.level || "all"} onValueChange={(v) => setFilter("level", v)}>
            <SelectTrigger className="w-40 bg-navy-surface border-white/15 text-slate-200" data-testid="filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
              {["all", "Beginner", "Intermediate", "Advanced", "Degree", "All Levels"].map((l) => (
                <SelectItem key={l} value={l}>{l === "all" ? "Any Level" : l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.mode || "all"} onValueChange={(v) => setFilter("mode", v)}>
            <SelectTrigger className="w-40 bg-navy-surface border-white/15 text-slate-200" data-testid="filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
              {["all", "Offline", "Online", "Hybrid", "Work-Integrated"].map((m) => (
                <SelectItem key={m} value={m}>{m === "all" ? "Any Mode" : m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.sort} onValueChange={(v) => setFilter("sort", v)}>
            <SelectTrigger className="w-44 bg-navy-surface border-white/15 text-slate-200" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="new">Newest</SelectItem>
              <SelectItem value="duration">Shortest First</SelectItem>
              <SelectItem value="price">Lowest Fee</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 ml-auto" data-testid="courses-count">{data.total} programs found</span>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm py-20 text-center" data-testid="courses-loading">Loading courses…</div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-20" data-testid="courses-empty">
            <p className="text-slate-400 mb-4">No courses match your search. Try different keywords or talk to our AI counsellor.</p>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full" data-testid="courses-clear-btn">
              <button onClick={() => { setQ(""); setSearchParams({}); }}>Clear Filters</button>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="courses-grid">
            {data.items.map((c) => <CourseCard key={c.slug} course={c} />)}
          </div>
        )}

        {data.pages > 1 && (
          <div className="flex justify-center gap-2 mt-12" data-testid="courses-pagination">
            {Array.from({ length: data.pages }, (_, i) => (
              <button key={i} onClick={() => setFilter("page", String(i + 1))}
                className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${filters.page === i + 1 ? "bg-orange-600 text-white" : "bg-navy-surface text-slate-300 border border-white/10 hover:border-orange-500/40"}`}
                data-testid={`page-btn-${i + 1}`}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
